// src/components/DocToneSelect.js
import React, { useMemo, useRef, useState } from "react";

/**
 * DocToneSelect (React 17+ compatible)
 * - Tone library (38 tones) with categories & descriptions.
 * - Reading level control (Plain, Standard, Professional, Academic).
 * - Inline CSS only.
 *
 * Audience is intentionally owned by the dedicated DocAudienceSelect component.
 *
 * Props (optional; self-manages if omitted):
 *  - value: string toneKey
 *  - onChange: (toneKey) => void
 *  - readingLevel: "plain" | "standard" | "professional" | "academic"
 *  - onReadingLevelChange: (level) => void
 *  - disabled?: boolean
 *  - namePrefix?: string   // optional: force a stable radio group name
 */

const DocToneSelect = ({
  value,
  onChange,
  readingLevel,
  onReadingLevelChange,
  disabled = false,
  namePrefix,
}) => {
  // ===== Tone Library (38 total) =====
  const tones = useMemo(
    () => [
      // LEGAL / PROFESSIONAL
      { key: "formal", label: "Formal", category: "Legal / Professional", description: "Strictly professional; suitable for courts, agencies, official letters." },
      { key: "neutral", label: "Neutral", category: "Legal / Professional", description: "Balanced, factual, objective; avoids emotional language." },
      { key: "legalistic", label: "Legalistic", category: "Legal / Professional", description: "Structured legal phrasing; cites rules/authorities; precise terms." },
      { key: "concise", label: "Concise", category: "Legal / Professional", description: "Shortest clear version; bullets/short paragraphs for quick reading." },
      { key: "assertive", label: "Assertive", category: "Legal / Professional", description: "Direct positions/requests; firm but not confrontational." },
      { key: "firm", label: "Firm", category: "Legal / Professional", description: "Sets boundaries and deadlines; unambiguous expectations." },
      { key: "persuasive", label: "Persuasive", category: "Legal / Professional", description: "Argues a position with evidence; addresses counterpoints." },
      { key: "diplomatic", label: "Diplomatic", category: "Legal / Professional", description: "Balances interests; softens disagreements; seeks cooperation." },
      { key: "professional", label: "Professional", category: "Legal / Professional", description: "Workplace-appropriate, direct, courteous; HR/admin friendly." },

      // BUSINESS / CUSTOMER
      { key: "customer_service", label: "Customer-Service", category: "Business / Customer", description: "Polite, solution-oriented; acknowledges inconvenience; next steps." },
      { key: "collaborative", label: "Collaborative", category: "Business / Customer", description: "Team-focused; proposes shared goals, timelines, responsibilities." },
      { key: "marketing", label: "Marketing", category: "Business / Customer", description: "Benefit-focused, positive; persuasive but softer than legal tone." },
      { key: "authoritative", label: "Authoritative", category: "Business / Customer", description: "Expert voice, confident; references policy or expertise." },
      { key: "negotiation", label: "Negotiation", category: "Business / Customer", description: "Trades/concessions, BATNA-aware; proposes middle ground." },
      { key: "settlement_oriented", label: "Settlement-Oriented", category: "Business / Customer", description: "De-escalates; outlines terms, releases, mutual benefits." },
      { key: "executive_summary", label: "Executive-Summary", category: "Business / Customer", description: "1-page brief: key points, risks, options, next steps." },
      { key: "compliance", label: "Compliance", category: "Business / Customer", description: "Aligns to policy/regulation; audit-friendly phrasing." },

      // ACADEMIC / TECHNICAL
      { key: "academic", label: "Academic", category: "Academic / Technical", description: "Scholarly register; references/footnotes when applicable." },
      { key: "analytical", label: "Analytical", category: "Academic / Technical", description: "Breaks down issues; compares options; logical structure." },
      { key: "explanatory", label: "Explanatory", category: "Academic / Technical", description: "Step-by-step explanations; defines terms for non-experts." },
      { key: "critical", label: "Critical", category: "Academic / Technical", description: "Evaluates strengths/weaknesses; recommends improvements." },
      { key: "procedural", label: "Procedural", category: "Academic / Technical", description: "Process-driven; timelines, forms, deadlines, responsibilities." },
      { key: "evidentiary", label: "Evidentiary", category: "Academic / Technical", description: "Organizes facts/exhibits; cites sources; chain-of-custody aware." },
      { key: "instructional", label: "Instructional", category: "Academic / Technical", description: "How-to guidance; checklists; action steps; tips/warnings." },

      // HUMAN / EMOTIONAL
      { key: "friendly", label: "Friendly", category: "Human / Emotional", description: "Warm, approachable; conversational but professional." },
      { key: "empathetic", label: "Empathetic", category: "Human / Emotional", description: "Validates feelings/hardship; supportive phrasing." },
      { key: "apologetic", label: "Apologetic", category: "Human / Emotional", description: "Accepts responsibility; offers remedy; requests understanding." },
      { key: "encouraging", label: "Encouraging", category: "Human / Emotional", description: "Uplifting; focuses on progress and achievable steps." },
      { key: "sympathetic", label: "Sympathetic", category: "Human / Emotional", description: "Gentle; expresses care and understanding." },
      { key: "reassuring", label: "Reassuring", category: "Human / Emotional", description: "Reduces anxiety; emphasizes plans and control." },

      // PROCEDURAL / RISK
      { key: "cautionary", label: "Cautionary", category: "Procedural / Risk", description: "Flags risks/uncertainty; prudent language; contingency notes." },
      { key: "stern", label: "Stern", category: "Procedural / Risk", description: "Stronger than Firm; still professional and non-hostile." },
      { key: "investigative", label: "Investigative", category: "Procedural / Risk", description: "Fact-finding questions; logs; neutral probes." },
      { key: "urgent", label: "Urgent", category: "Procedural / Risk", description: "Time-sensitive; highlights deadlines and consequences." },
      { key: "polite_refusal", label: "Polite Refusal", category: "Procedural / Risk", description: "Declines requests respectfully; cites reasons/policy; alternatives." },

      // EVERYDAY / GENERAL
      { key: "plain_business", label: "Plain-Business", category: "Everyday / General", description: "Office-ready plain English; direct, unadorned, practical." },
      { key: "conversational", label: "Conversational", category: "Everyday / General", description: "Light professional; clear and human without slang." },
      { key: "courteous", label: "Courteous", category: "Everyday / General", description: "Extra polite formulations; gratitude and positive framing." },
      { key: "celebratory", label: "Celebratory", category: "Everyday / General", description: "Congratulatory; brief, warm notes for milestones." }
    ],
    []
  );

  // Group tones by category (rename to avoid global name collisions)
  const toneGroups = useMemo(() => {
    const byCat = {};
    for (const t of tones) {
      if (!byCat[t.category]) byCat[t.category] = [];
      byCat[t.category].push(t);
    }
    for (const cat in byCat) byCat[cat].sort((a, b) => a.label.localeCompare(b.label));
    return byCat;
  }, [tones]);

  // Controlled/uncontrolled safety
  const [localTone, setLocalTone] = useState(value ?? "formal");
  const [localLevel, setLocalLevel] = useState(readingLevel ?? "standard");

  const selectedToneKey = value ?? localTone;
  const selectedLevel = readingLevel ?? localLevel;

  const selectedTone =
    tones.find((t) => t.key === selectedToneKey) || tones.find((t) => t.key === "formal");

  const handleToneChange = (e) => {
    const next = e.target.value;
    onChange?.(next);
    if (value === undefined) setLocalTone(next);
  };
  const handleLevelChange = (e) => {
    const next = e.target.value;
    onReadingLevelChange?.(next);
    if (readingLevel === undefined) setLocalLevel(next);
  };
  // Unique, stable radio group name (React 17 friendly)
  const idRef = useRef(
    namePrefix
      ? `reading-level-${namePrefix}`
      : `reading-level-${Math.random().toString(36).slice(2, 9)}`
  );
  const levelName = idRef.current;

  // Styles
  const s = {
    wrap: { border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, background: "#fff" },
    sectionTitle: { fontWeight: 700, marginBottom: 8, fontSize: 14 },
    column: { flex: 1, minWidth: 260 },
    select: { width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 14, background: "#fff" },
    optgroup: { fontWeight: 600, background: "#f9fafb", color: "#111827" },
    help: { fontSize: 12, color: "#6b7280", marginTop: 6 },
    card: { border: "1px solid #e5e7eb", background: "#fafafa", borderRadius: 10, padding: 12, marginTop: 12 },
    badge: { display: "inline-block", fontSize: 11, padding: "4px 8px", borderRadius: 999, border: "1px solid #e5e7eb", background: "#f9fafb", marginRight: 8 },
    label: { display: "block", fontWeight: 600, marginBottom: 6, fontSize: 13 },
    radioRow: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginTop: 4 },
  };

  return (
    <div style={s.wrap}>
      {/* Tone */}
      <div style={s.column}>
        <div style={s.sectionTitle}>Select Tone</div>
        <select
          value={selectedToneKey}
          onChange={handleToneChange}
          style={s.select}
          aria-label="Tone select"
          aria-describedby="tone-desc"
          disabled={disabled}
        >
          <option value="" disabled>-- Choose a tone --</option>
          {Object.entries(toneGroups).map(([cat, items]) => (
            <optgroup key={cat} label={cat} style={s.optgroup}>
              {items.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </optgroup>
          ))}
        </select>

        <div style={s.card} aria-live="polite" id="tone-desc">
          <div>
            <span style={s.badge}>{selectedTone?.category}</span>
            <span style={s.badge}>{selectedTone?.label}</span>
          </div>
          <div style={{ marginTop: 6, fontSize: 13, color: "#374151" }}>
            {selectedTone?.description}
          </div>
          <div style={s.help}>Tip: You can change tone later during revisions without losing your work.</div>
        </div>
      </div>

      {/* Reading Level */}
      <div style={{ ...s.column, marginTop: 16 }}>
        <label style={s.label}>Reading Level</label>
        <div style={s.radioRow}>
          <label><input type="radio" name={levelName} value="plain" checked={selectedLevel === "plain"} onChange={handleLevelChange} disabled={disabled} /> Plain (Grade ~6)</label>
          <label><input type="radio" name={levelName} value="standard" checked={selectedLevel === "standard"} onChange={handleLevelChange} disabled={disabled} /> Standard</label>
          <label><input type="radio" name={levelName} value="professional" checked={selectedLevel === "professional"} onChange={handleLevelChange} disabled={disabled} /> Professional</label>
          <label><input type="radio" name={levelName} value="academic" checked={selectedLevel === "academic"} onChange={handleLevelChange} disabled={disabled} /> Academic</label>
        </div>
        <div style={s.help}>Reading level controls vocabulary, sentence length, and structure.</div>
      </div>
    </div>
  );
};

export default DocToneSelect;
